<script setup lang="ts">
import { computed } from 'vue'
import type { UsageWeek } from '../stores/usage'
import UsageWeeksChart from './UsageWeeksChart.vue'
import {
  cacheHitRate,
  formatTokens,
  formatUsd,
  modelColor,
  shortModel,
  totalTokens,
} from '../composables/usageFormatting'
import { aggregateCacheHitRate, formatWeekStart, operationTone, shareOfMax } from './UsageSpendBars'

// The weekly-spend half of UsageView.vue (#1090): the "This week so far"
// card (total, spend-by-operation bars, cache hit rate, per-model split)
// and the "Previous weeks" chart card. Renders two sibling cards so the
// parent's auto-fit grid can lay them side by side on wide screens.
const props = defineProps<{
  currentWeek: UsageWeek | null
  pastWeeks: UsageWeek[]
}>()

const operations = computed(() => props.currentWeek?.operations ?? [])
const maxOperationCost = computed(() => Math.max(0, ...operations.value.map((op) => op.estimated_cost_cents)))
const overallCacheRate = computed(() => aggregateCacheHitRate(props.currentWeek?.models ?? []))
const weekTokens = computed(() => (props.currentWeek?.models ?? []).reduce((acc, m) => acc + totalTokens(m), 0))
</script>

<template>
  <section
    v-if="currentWeek"
    data-testid="this-week-so-far"
    aria-labelledby="usage-this-week-heading"
    class="flex flex-col gap-3.5 rounded-2xl border border-border-subtle bg-surface-raised p-4 sm:p-5"
  >
    <div class="flex flex-col gap-0.5">
      <div class="flex items-baseline gap-2.5">
        <h2 id="usage-this-week-heading" class="m-0 flex-1 text-[15px] font-semibold text-ink">This week so far</h2>
        <span class="font-mono text-[22px] font-medium text-ink tabular-nums">
          {{ formatUsd(currentWeek.total_cost_cents) }}
        </span>
      </div>
      <p class="m-0 text-xs text-ink-faint">
        Week of {{ formatWeekStart(currentWeek.week_start) }} · across {{ currentWeek.models.length }} model{{
          currentWeek.models.length === 1 ? '' : 's'
        }}
      </p>
    </div>

    <!-- Per-operation cost breakdown: which feature drives spend.
         Current-week only — the backend derives it live. -->
    <div v-if="operations.length > 0" data-testid="operation-breakdown" class="flex flex-col gap-1">
      <h3 class="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">By operation</h3>
      <ul class="m-0 list-none p-0">
        <li
          v-for="op in operations"
          :key="op.operation"
          data-testid="operation-row"
          class="grid min-h-7 grid-cols-[88px_1fr_64px] items-center gap-3"
          :title="`${formatTokens(totalTokens(op))} tokens`"
        >
          <span class="capitalize text-ink-muted">{{ op.operation }}</span>
          <span class="block h-2.5 overflow-hidden rounded-[5px] bg-border-divider" aria-hidden="true">
            <span
              class="block h-full rounded-[inherit]"
              :class="operationTone(op.operation)"
              :style="{ width: `${shareOfMax(op.estimated_cost_cents, maxOperationCost)}%` }"
            />
          </span>
          <span class="text-right font-mono text-xs text-ink tabular-nums">{{
            formatUsd(op.estimated_cost_cents)
          }}</span>
        </li>
      </ul>
    </div>

    <div class="flex flex-wrap gap-x-6 gap-y-2 border-t border-border-divider pt-2.5">
      <div>
        <div class="text-xs text-ink-faint">Cache hit rate</div>
        <div data-testid="cache-hit-rate-overall" class="mt-0.5 font-mono text-lg text-ink tabular-nums">
          {{ overallCacheRate === null ? '—' : `${overallCacheRate}%` }}
        </div>
      </div>
      <div>
        <div class="text-xs text-ink-faint">Tokens</div>
        <div class="mt-0.5 font-mono text-lg text-ink tabular-nums">{{ formatTokens(weekTokens) }}</div>
      </div>
    </div>

    <div v-if="currentWeek.models.length > 0" class="flex flex-col gap-1">
      <h3 class="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">By model</h3>
      <ul class="m-0 list-none p-0">
        <li
          v-for="m in currentWeek.models"
          :key="m.model"
          class="flex min-h-9 flex-wrap items-center gap-x-2 gap-y-0.5 border-t border-border-divider py-1.5 text-xs"
        >
          <span class="inline-block size-2 shrink-0 rounded-sm" :class="modelColor(m.model)" aria-hidden="true" />
          <span class="flex-1 font-mono text-ink">{{ shortModel(m.model) }}</span>
          <span class="text-ink-faint tabular-nums">{{ formatTokens(totalTokens(m)) }} tokens</span>
          <span
            v-if="cacheHitRate(m) !== null"
            data-testid="cache-hit-rate"
            class="text-ink-muted tabular-nums"
            title="Prompt-cache hit rate — share of input served from cache"
            >· {{ cacheHitRate(m) }}% cached</span
          >
          <span class="w-14 text-right font-mono text-ink tabular-nums">{{ formatUsd(m.estimated_cost_cents) }}</span>
        </li>
      </ul>
    </div>
  </section>

  <UsageWeeksChart v-if="pastWeeks.length > 0" :current-week="currentWeek" :past-weeks="pastWeeks" />
</template>
